import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';

import { appWriteUser } from '@/hooks/useAuth';

import { clearSession } from '@/components/shared/AppWriteLocalStroage';
import {
  setToken,
  setUserId,
  setUserName,
  setZl_UID,
} from '@/components/shared/getToken';
import CustomLoader from '@/components/shared/Loading';
import { resetSessionExistsCookie } from '@/components/shared/ManageTokenForClient/manageCookies';
import { notify } from '@/components/shared/notify';

import { tokenUpdate } from '@/atoms/authModalControl';
import { user } from '@/atoms/user';
import appwriteService, { setTokenInCookies } from '@/config/appwrite';
import { flushAndHydrateGuestEvents } from '@/services/Events/eventBulkProcessor';
import { AppwriteUserData, User } from '@/utils/interfaces/User';

export default function AuthCallbackPage() {
  const [, setUserData] = useRecoilState<User | undefined>(user);
  const [, setIsTokenChanged] = useRecoilState(tokenUpdate);

  const router = useRouter();
  const { secret, zl_uid } = router.query;

  useEffect(() => {
    if (!secret || !zl_uid) {
      return;
    }

    const completeAuthFlow = async () => {
      try {
        await appwriteService.logoutIfSessionExists();

        await appwriteService.createSessionBySecretKey({
          user_id: zl_uid as string,
          secret: secret as string,
        });

        const token = await setTokenInCookies();
        if (!token) throw new Error('Token generation failed');

        let currentUser: AppwriteUserData | undefined = undefined;

        if (token) {
          const user = await appwriteService.getCurrentUser();
          if (user !== null) {
            currentUser = user as unknown as AppwriteUserData;
          }
        }

        const dbUser: User = await appWriteUser(currentUser?.userId as string);

        await setToken(token as string);
        await setUserName(dbUser?.name);
        setUserData(dbUser);
        setIsTokenChanged(true);
        await setUserId(dbUser?.id?.toString());
        await setZl_UID(dbUser?.zl_uid?.toString() as string);

        if (dbUser?.zl_uid) {
          flushAndHydrateGuestEvents({
            zl_uid: dbUser.zl_uid.toString(),
            userId: dbUser?.id?.toString(),
          });
        }

        resetSessionExistsCookie();
        clearSession();

        router.replace('/');
      } catch (err) {
        if (typeof window !== undefined) {
          notify('You are not authenticated', 'error');

          const redirectUrl = decodeURIComponent(window.location.pathname);
          window.location.href = `/auth/login?redirectUrl=${redirectUrl}`;
        }
      }
    };

    completeAuthFlow();
  }, [secret, zl_uid]);

  return <CustomLoader />;
}
